import { useMemo } from 'react';
import {
  Line,
  LineChart,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import { format } from 'date-fns';
import { Link } from 'react-router';
import type { StatusTransition } from '@/hooks/useDeviceTrafficSeries';
import type { Incident } from '@/hooks/useIncidents';
import { incidentBands, statusSpans, type StatusSpan } from './rows';
import { CHART_MARGIN, Y_AXIS_WIDTH } from './chart';

const LANE_HEIGHT = 32;

/** Two anchor points so the lane has an x range and a y scale to draw against. */
const LANE_DATA = (start: number, end: number) => [
  { ts: start, y: 0 },
  { ts: end, y: 1 },
];

/**
 * When each thing happened, on the same x-axis as everything above it.
 *
 * Flaps and incident windows drawn as reference lines *on* the traffic chart
 * fail at three or more events — the labels collide and the plot becomes a
 * picket fence. A dedicated strip gives every event room, and because it shares
 * the surface's `syncId` the crosshair *proves* that a discard burst sits on
 * the traffic knee rather than leaving the reader to eyeball two charts.
 *
 * Two lanes, and only the ones with something in them.
 *
 * There is deliberately no counter-discontinuity lane. Resets are excised in
 * the query pipeline and arrive as a `null`, which is indistinguishable from a
 * missed poll — so a lane claiming "the counter reset here" would be asserting
 * something the wire cannot support.
 */
export function EventRail({
  start,
  end,
  syncId,
  statusBefore,
  transitions,
  incidents,
}: {
  start: number;
  end: number;
  syncId: string;
  statusBefore: string | null;
  transitions: StatusTransition[];
  incidents: Incident[];
}) {
  const spansDays = end - start > 24 * 3600 * 1000;
  const spans = useMemo(
    () => statusSpans(statusBefore, transitions, start, end),
    [statusBefore, transitions, start, end],
  );
  const notUp = spans.filter((span) => span.state !== 'up');

  const windows = useMemo(() => incidentBands(incidents, start, end), [incidents, start, end]);

  const hasLinkLane = transitions.length > 0 || notUp.length > 0;
  if (!hasLinkLane && windows.length === 0) return null;

  return (
    <div>
      <h3 className="text-xs font-semibold mb-2">Events</h3>

      {hasLinkLane ? (
        <Lane
          label="Link state"
          start={start}
          end={end}
          syncId={syncId}
          bands={notUp.map((span) => ({
            from: span.from,
            to: span.to,
            fill: 'var(--sev-1)',
          }))}
          marks={transitions.map((transition) => ({
            ts: transition.ts,
            stroke: transition.to === 'up' ? 'var(--sev-4)' : 'var(--sev-1)',
          }))}
        />
      ) : null}

      {windows.length > 0 ? (
        <Lane
          label="Incidents on this device"
          start={start}
          end={end}
          syncId={syncId}
          bands={windows.map((w) => ({ from: w.drawFrom, to: w.drawTo, fill: 'var(--sev-1)' }))}
          marks={[]}
        />
      ) : null}

      {/* The events as text. This is the lane's hit target — a 1px reference
          line is not one — and it is also the table twin the surface owes every
          block. */}
      <ul className="mt-2 space-y-1">
        {transitions.map((transition) => (
          <li key={`t-${String(transition.ts)}`} className="text-xs text-muted-foreground">
            <span className="tabular-nums text-foreground">
              {format(new Date(transition.ts), spansDays ? 'MMM d, HH:mm' : 'HH:mm:ss')}
            </span>{' '}
            link went {transition.to} (from {transition.from})
          </li>
        ))}
        {spansOnlyNote(notUp, transitions, spansDays)}
        {windows.map((band) => (
          <li key={band.id} className="text-xs text-muted-foreground">
            <span className="tabular-nums text-foreground">
              {format(new Date(band.from), spansDays ? 'MMM d, HH:mm' : 'HH:mm')}
            </span>
            {band.open
              ? ' — open'
              : ` – ${format(new Date(band.to), spansDays ? 'MMM d, HH:mm' : 'HH:mm')}`}
            {band.clipped ? ' (began before this window)' : ''}{' '}
            <Link to={`/incidents/${band.id}`} className="text-foreground underline underline-offset-2">
              {band.title}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * A port that was down for the whole window has no transition to list, and the
 * band alone would say nothing about when it started. Saying it began before
 * the window is the honest phrasing — the series cannot see further back.
 */
function spansOnlyNote(notUp: StatusSpan[], transitions: StatusTransition[], spansDays: boolean) {
  if (transitions.length > 0) return null;
  const leading = notUp.find((span) => span.openStart);
  if (!leading) return null;
  return (
    <li className="text-xs text-muted-foreground">
      Link was {leading.state} for the whole window
      {spansDays ? ` (through ${format(new Date(leading.to), 'MMM d, HH:mm')})` : ''}.
    </li>
  );
}

/**
 * One 32px lane. Axes are hidden but the y-axis still reserves the same gutter
 * as the charts above, so the lane's x-positions line up with theirs — the
 * whole reason the rail sits under them.
 */
function Lane({
  label,
  start,
  end,
  syncId,
  bands,
  marks,
}: {
  label: string;
  start: number;
  end: number;
  syncId: string;
  bands: { from: number; to: number; fill: string }[];
  marks: { ts: number; stroke: string }[];
}) {
  return (
    <div className="mb-1">
      <p className="text-[11px] font-medium text-muted-foreground">{label}</p>
      <ResponsiveContainer width="100%" height={LANE_HEIGHT}>
        <LineChart data={LANE_DATA(start, end)} syncId={syncId} syncMethod="value" margin={CHART_MARGIN}>
          <XAxis dataKey="ts" type="number" scale="time" domain={[start, end]} hide />
          <YAxis width={Y_AXIS_WIDTH} domain={[0, 1]} tick={false} axisLine={false} />
          {/* A lane draws no data of its own, only bands and marks. Recharts
              still needs a plotted series to build a y scale against — without
              one it silently rendered no reference areas at all, while the
              chart, the axes and the surface all appeared. Hence a transparent
              line, and explicit y bounds on every band rather than leaving them
              to be inferred from a scale that may not exist. */}
          <Line dataKey="y" stroke="none" dot={false} isAnimationActive={false} />
          {/* Keyed by index, not by timestamp. `incidentBands` clamps both ends
              to the visible window (`Math.max(from, start)` /
              `Math.min(to, end)`), so any two incidents that were already open
              when the window began collapse to the SAME from/to and collided on
              a timestamp key — React then dropped one of the bands. The list is
              rebuilt whole from a memo on every render and never reordered
              independently of its contents, and a ReferenceArea holds no
              internal state, so positional keys are correct here. */}
          {bands.map((band, index) => (
            <ReferenceArea
              key={`b-${String(index)}`}
              x1={band.from}
              x2={band.to}
              y1={0}
              y2={1}
              fill={band.fill}
              fillOpacity={0.18}
              ifOverflow="hidden"
            />
          ))}
          {marks.map((mark, index) => (
            <ReferenceLine
              key={`m-${String(index)}`}
              x={mark.ts}
              stroke={mark.stroke}
              strokeWidth={2}
              ifOverflow="hidden"
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
